import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import LoginForm from '@/components/auth/LoginForm';

import { z } from 'zod';

// Define the login schema first
const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6)
});

// Then infer the type from the schema
type LoginFormValues = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Handle navigation after successful login
  useEffect(() => {
    if (user && isAuthenticated) {
      // Navigate immediately - no delay needed
      if (user.role === 'doctor') {
        navigate('/doctor-dashboard', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, isAuthenticated, navigate]);
  
  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    
    try {
      const success = await login({
        email: data.email,
        password: data.password
      });
      
      if (success) {
        toast({
          title: "Welcome",
          description: "Welcome to AMCare",
        });
      } else {
        toast({
          title: "Login failed",
          description: "Invalid email or password",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Login error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        background: `
          linear-gradient(135deg, 
            #87CEEB 0%, 
            #98D8E8 15%, 
            #B0E0E6 30%, 
            #E0F6FF 45%, 
            #F0F8FF 60%, 
            #FFF8DC 75%, 
            #F5DEB3 90%, 
            #DEB887 100%
          )
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Subtle overlay for better text readability */}
      <div className="absolute inset-0 bg-black/10"></div>
      
      <div className="w-full max-w-sm relative z-10">
        <Card className="shadow-2xl bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl">
          <CardHeader className="pb-6">
            {/* AMCare Logo at top of card */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="rounded-lg bg-hospital-primary p-2 shadow-sm">
                <h2 className="text-white font-bold text-lg">AM</h2>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">AMCare</h2>
            </div>
            
            <CardTitle className="text-gray-900 text-2xl font-bold text-center">Welcome Back</CardTitle>
            <CardDescription className="text-gray-600 text-center">
              Sign in to your account
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-6">
            <LoginForm 
              defaultEmail=""
              defaultPassword=""
              onSubmit={onSubmit}
              isLoading={isLoading}
              userRole="User"
            />
          </CardContent>
          <CardFooter className="pt-4 pb-6">
            <div className="text-center w-full">
              <p className="text-sm text-gray-500">
                Your trusted healthcare partner
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Login;
